// Shared design tokens for partner portal inline styles.
// Single source of truth: no more per-page duplication.

import type { CSSProperties } from 'react';

/* ── Colours ─────────────────────────────────────── */

export const colors = {
  bg: '#0a0e1a',
  cardBg: 'rgba(15, 23, 42, 0.6)',
  cardBgLight: 'rgba(15, 23, 42, 0.4)',
  cardBgDark: 'rgba(15, 23, 42, 0.8)',
  border: 'rgba(148, 163, 184, 0.1)',
  borderLight: 'rgba(148, 163, 184, 0.2)',
  borderSubtle: 'rgba(148, 163, 184, 0.08)',
  cyan: '#67e8f9',
  cyanDark: '#06b6d4',
  cyanMid: '#0ea5e9',
  cyanBg: 'rgba(103, 232, 249, 0.08)',
  cyanBorder: 'rgba(103, 232, 249, 0.2)',
  cyanBtnBg: 'rgba(6, 182, 212, 0.15)',
  cyanBtnBorder: 'rgba(6, 182, 212, 0.3)',
  text: '#e2e8f0',
  muted: '#94a3b8',
  mutedLight: '#64748b',
  mutedLighter: '#475569',
  success: '#6ee7b7',
  successAlt: '#34d399',
  successBg: 'rgba(16, 185, 129, 0.1)',
  successBorder: 'rgba(16, 185, 129, 0.25)',
  successBgLight: 'rgba(16, 185, 129, 0.15)',
  warning: '#fcd34d',
  warningAlt: '#fbbf24',
  warningBg: 'rgba(251, 191, 36, 0.1)',
  warningBorder: 'rgba(251, 191, 36, 0.25)',
  warningBgLight: 'rgba(250, 204, 21, 0.15)',
  danger: '#fda4af',
  dangerAlt: '#f87171',
  dangerBg: 'rgba(244, 63, 94, 0.1)',
  dangerBorder: 'rgba(244, 63, 94, 0.3)',
  dangerBgLight: 'rgba(244, 63, 94, 0.15)',
  infoBg: 'rgba(59, 130, 246, 0.15)',
  infoText: '#60a5fa',
  purpleBg: 'rgba(168, 85, 247, 0.15)',
  purpleText: '#c084fc',
  neutralBg: 'rgba(148, 163, 184, 0.15)',
  neutralText: '#94a3b8',
  neutralBgLight: 'rgba(107, 114, 128, 0.15)',
  neutralTextLight: '#9ca3af',
} as const;

/* ── Shared style objects ────────────────────────── */

export const cardStyle: CSSProperties = {
  padding: '20px',
  borderRadius: '12px',
  background: colors.cardBg,
  border: `1px solid ${colors.border}`,
  marginBottom: '16px',
};

export const cardStyleLg: CSSProperties = {
  ...cardStyle,
  padding: '24px',
  marginBottom: '24px',
};

export const labelStyle: CSSProperties = {
  fontFamily: 'monospace',
  fontSize: '10px',
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: colors.cyan,
  marginBottom: '8px',
};

export const labelBlockStyle: CSSProperties = {
  ...labelStyle,
  marginBottom: '6px',
  display: 'block',
};

export const valueStyle: CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 600,
  color: colors.text,
};

export const mutedStyle: CSSProperties = {
  color: colors.muted,
  fontSize: '0.85rem',
  lineHeight: 1.6,
};

export const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: '8px',
  border: `1px solid ${colors.borderLight}`,
  background: colors.cardBgLight,
  color: colors.text,
  fontSize: '0.85rem',
  marginBottom: '12px',
  boxSizing: 'border-box',
  outline: 'none',
};

export const headingStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 600,
  margin: '0 0 24px',
};

export const pageWrapperStyle: CSSProperties = {
  padding: '32px',
  maxWidth: '900px',
};

export const linkStyle: CSSProperties = {
  color: colors.cyan,
  textDecoration: 'underline',
};

export const footerStyle: CSSProperties = {
  marginTop: '32px',
  padding: '16px 20px',
  borderRadius: '12px',
  background: colors.cardBgLight,
  border: `1px solid ${colors.borderSubtle}`,
  textAlign: 'center',
  fontSize: '0.75rem',
  color: colors.mutedLight,
};

export const errorBannerStyle: CSSProperties = {
  ...cardStyle,
  borderColor: colors.dangerBorder,
  color: colors.danger,
  fontSize: '0.85rem',
};

export const successTextStyle: CSSProperties = {
  color: colors.success,
  fontSize: '0.85rem',
};
